import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videoApi, dmmApi } from '../utils/apiConfig';
import { generateAffiliateUrl, generateMGSAffiliateUrl } from '../utils/affiliateHelper';

const VideoDetailNew = () => {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    const fetchVideoDetail = async () => {
      try {
        setLoading(true);
        const response = await videoApi.getById(id);
        const videoData = response.data;
        
        // Parse tags if they're stored as JSON string
        if (videoData.tags && typeof videoData.tags === 'string') {
          try {
            videoData.tags = JSON.parse(videoData.tags);
          } catch (e) {
            console.error('Failed to parse tags:', e);
            videoData.tags = [];
          }
        }
        
        // Parse genres if they're stored as JSON string
        if (videoData.genres && typeof videoData.genres === 'string') {
          try {
            videoData.genres = JSON.parse(videoData.genres);
          } catch (e) {
            console.error('Failed to parse genres:', e);
            videoData.genres = [];
          }
        }
        
        setVideo(videoData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch video details');
        setLoading(false);
        console.error(err);
      }
    };

    fetchVideoDetail();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '不明';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  const handleDmmSync = async () => {
    setSyncing(true);
    setSyncStatus(null);

    try {
      const response = await dmmApi.syncVideo(id);
      setSyncStatus(response.data);
      
      // Refresh video data
      const videoResponse = await videoApi.getById(id);
      setVideo(videoResponse.data);
    } catch (err) {
      setSyncStatus({
        status: 'error',
        message: err.response?.data?.message || 'Sync failed'
      });
      console.error('Sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error}</span>
    </div>
  );
  
  if (!video) return (
    <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
      <span className="block sm:inline">Video not found</span>
    </div>
  );

  return (
    <div className="video-detail-new min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* 背景装飾 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-pulse"></div>
      </div>
      
      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        {/* パンくずリスト */}
        <nav className="mb-8">
          <div className="inline-flex items-center space-x-2 text-sm bg-white/80 backdrop-blur-lg rounded-full px-6 py-3 shadow-lg">
            <Link to="/" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
              ホーム
            </Link>
            <span className="text-gray-400">&gt;</span>
            <Link to="/videos" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
              作品一覧
            </Link>
            <span className="text-gray-400">&gt;</span>
            <span className="text-gray-600">{video.productCode}</span>
          </div>
        </nav>

        {/* 作品ジャケットとタイトル */}
        <div className="text-center mb-12">
          <div className="relative inline-block group">
            <div className="w-full max-w-md mx-auto">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl transform transition-all duration-500 group-hover:scale-105">
                {video.coverImage ? (
                  <img 
                    src={video.coverImage} 
                    alt={video.title} 
                    className="w-full h-auto"
                    key={`${video.id}-${video.productCode}`}
                  />
                ) : (
                  <div className="w-full aspect-[3/4] bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <span className="text-white text-2xl font-light tracking-wider">No Image</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold mt-8 mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient-x">
            {video.title}
          </h1>
          
          {/* ソースインジケーター */}
          <div className="flex gap-2 mt-4">
            {video.source === 'MGS' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                MGS限定
              </span>
            )}
            {video.source === 'DMM' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                FANZA
              </span>
            )}
          </div>
        </div>

        {/* メインコンテンツ */}
        <div className="max-w-4xl mx-auto">
          {/* 作品情報テーブル */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl p-8 mb-8 border border-purple-100">
            <div className="space-y-6">
              {/* 出演女優 */}
              {video.actresses && video.actresses.length > 0 && (
                <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    出演女優
                  </span>
                  <div className="flex-1 flex flex-wrap gap-3">
                    {video.actresses.map(actress => (
                      <Link 
                        key={actress.id}
                        to={`/actresses/${actress.id}`} 
                        className="relative text-purple-600 font-semibold hover:text-purple-700 transition-all duration-300 group"
                      >
                        <span className="relative z-10">{actress.name}</span>
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600 group-hover:w-full transition-all duration-300"></span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* リリース日 */}
              <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                  リリース日
                </span>
                <span className="flex-1 text-gray-700 font-medium">{formatDate(video.releaseDate)}</span>
              </div>

              {/* 収録時間 */}
              {video.runtime && (
                <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    収録時間
                  </span>
                  <span className="flex-1 text-gray-700 font-medium">{video.runtime}分</span>
                </div>
              )}

              {/* 品番 */}
              <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                  品番
                </span>
                <span className="flex-1 text-gray-700 font-medium">{video.productCode}</span>
              </div>

              {/* メーカー */}
              {video.maker && (
                <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    メーカー
                  </span>
                  <span className="flex-1 text-gray-700 font-medium">{video.maker}</span>
                </div>
              )}

              {/* レーベル */}
              {video.label && (
                <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    レーベル
                  </span>
                  <span className="flex-1 text-gray-700 font-medium">{video.label}</span>
                </div>
              )}

              {/* シリーズ */}
              {video.series && (
                <div className="flex flex-col sm:flex-row border-b border-purple-100 pb-6">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    シリーズ
                  </span>
                  <span className="flex-1 text-gray-700 font-medium">{video.series}</span>
                </div>
              )}

              {/* ジャンル・タグ */}
              {video.tags && Array.isArray(video.tags) && video.tags.length > 0 && (
                <div className="flex flex-col sm:flex-row">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider w-32 mb-2 sm:mb-0">
                    ジャンル
                  </span>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {video.tags.map((tag, index) => (
                      <span 
                        key={index} 
                        className="inline-block bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold hover:from-purple-600 hover:to-pink-600 hover:text-white transform hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer border border-purple-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 購入ボタン */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            {video.dmmUrl && (
              <div className="text-center">
                <a 
                  href={generateAffiliateUrl(video.dmmUrl)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                  <span className="relative inline-flex items-center px-12 py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-bold rounded-full shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl">
                    <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    FANZAで購入・視聴する
                  </span>
                </a>
              </div>
            )}
            
            {video.mgsUrl && (
              <div className="text-center">
                <a 
                  href={generateMGSAffiliateUrl(video.mgsUrl)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                  <span className="relative inline-flex items-center px-12 py-5 bg-gradient-to-r from-red-600 to-orange-600 text-white text-lg font-bold rounded-full shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl">
                    <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    MGSで購入・視聴する
                  </span>
                </a>
              </div>
            )}
          </div>
          
          {(video.dmmUrl || video.mgsUrl) && (
            <p className="text-sm text-gray-500 text-center mb-8">※外部サイトへ移動します</p>
          )}


          {/* DMM同期情報 */}
          {video.dmmContentId && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-purple-100">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {video.lastSyncedAt && (
                    <span>最終同期: {formatDate(video.lastSyncedAt)}</span>
                  )}
                </div>
                <button
                  onClick={handleDmmSync}
                  disabled={syncing}
                  className="px-6 py-2 bg-gray-600 text-white text-sm rounded-full hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
                >
                  {syncing ? 'DMM同期中...' : 'DMM情報を更新'}
                </button>
              </div>
            </div>
          )}

          {syncStatus && (
            <div className={`mt-4 p-4 rounded-lg ${
              syncStatus.status === 'error' 
                ? 'bg-red-100 border border-red-200 text-red-700'
                : 'bg-green-100 border border-green-200 text-green-700'
            }`}>
              {syncStatus.status === 'error' ? (
                <div>
                  <strong>同期エラー:</strong> {syncStatus.message}
                </div>
              ) : (
                <div>
                  <strong>同期完了!</strong>
                  {syncStatus.changes && Object.keys(syncStatus.changes).length > 0 && (
                    <div className="mt-2 text-sm">
                      <div>更新された項目:</div>
                      {Object.entries(syncStatus.changes).map(([key, change]) => (
                        <div key={key} className="ml-2">
                          {key}: {change.old} → {change.new}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetailNew;